// The top of the item panel: the numbers that decide what to do with an item, before any field (available, with on hand
// in the tooltip; at the CMs; on order; and for a planned item its bucket, order-by date and cover), then chips that jump
// to the panel's sections. Each section the panel shows sits in a `data-sec` wrapper; a chip appears only for one that
// has something in it. Manufacturing projects --- a part's projects (the projects whose BOMs use it) as a line of chips.
import { useEffect, useMemo, useRef, useState } from "react";
import { currentRevision } from "../../../shared/boms.ts";
import type { Item } from "../../../shared/db.gen.ts";
import { BUCKET_LABELS, coverText, orderByText } from "../../../shared/planning.ts";
import { isOpen } from "../../../shared/po.ts";
import { hhlStock, stockNote } from "../../../shared/stock.ts";
import { qty } from "../lib/format.ts";
import { usePlan } from "../lib/planning.ts";
import { usePartProjects, useProjectIndex } from "../lib/projects.ts";
import { useRows } from "../lib/sync.ts";
import { Badge, cx } from "../ui/controls.tsx";
import { BUCKET_TONE } from "./ItemPlanSection.tsx";
import { PartProjectChips } from "./projects/ProjectChips.tsx";

export function ItemSummary({ item }: { item: Item }) {
  const levels = useRows("stock_level");
  const balances = useRows("stock_balance");
  const locations = useRows("location");
  const lines = useRows("po_line");
  const pos = useRows("po");
  const plan = usePlan();
  const r = plan.byItem.get(item.id);

  const s = useMemo(() => {
    const mine = <T extends { item_id: string }>(rows: readonly T[]) => rows.filter((x) => x.item_id === item.id);
    const myLevels = mine(levels);
    const myBalances = mine(balances);
    const hhl = hhlStock(myLevels, myBalances, locations).get(item.id);
    const cms = new Map(locations.filter((l) => l.is_cm && l.active && !l.ignored).map((l) => [l.id, l.name]));
    const atCm = [...myLevels.map((x) => ({ at: x.location_id, n: Number(x.on_hand) })), ...myBalances.map((x) => ({ at: x.location_id, n: Number(x.qty) }))]
      .filter((x) => cms.has(x.at))
      .reduce((n, x) => n + x.n, 0);
    const poById = new Map(pos.map((p) => [p.id, p]));
    const open = mine(lines).flatMap((l) => {
      const po = poById.get(l.po_id);
      return po && isOpen(po.status) ? [{ po: po.number, draft: po.status === "draft", left: Math.max(0, Number(l.qty) - Number(l.qty_received)) }] : [];
    });
    const sum = (draft: boolean) => open.filter((o) => o.draft === draft).reduce((n, o) => n + o.left, 0);
    return { hhl, atCm, onOrder: sum(false), inDrafts: sum(true), orderPos: open.map((o) => `${o.po}${o.draft ? " (draft)" : ""}: ${qty(o.left)}`) };
  }, [item.id, levels, balances, locations, lines, pos]);

  const note = stockNote(s.hhl);
  return (
    <div className="mx-1 mb-1 rounded-lg border border-line bg-surface-2/50 px-3 py-2">
      <div className="grid grid-cols-3 gap-x-3 gap-y-2">
        {item.shopify_tracked === false ? (
          // A labor SKU (a custom build's) or a service: Shopify doesn't track its stock, so there's none to show
          <Stat label="Available" title="Shopify doesn't track this one's stock: a labor or service item, so it has no stock here">
            <span className="font-sans text-[13px] font-normal text-faint">Not tracked</span>
          </Stat>
        ) : (
          <Stat label="Available" title={note ?? `${qty(s.hhl?.onHand ?? 0)} on hand at HHL`} danger={(s.hhl?.available ?? 0) < 0}>
            {qty(s.hhl?.available ?? 0) || "0"}
          </Stat>
        )}
        <Stat label="At CMs" title="HHL's stock held at contract manufacturers (not in available)" danger={s.atCm < 0}>
          {s.atCm ? qty(s.atCm) : <span className="text-faint">—</span>}
        </Stat>
        <Stat label="On order" title={s.orderPos.length ? s.orderPos.join("\n") : "Not on any open PO"}>
          {s.onOrder ? qty(s.onOrder) : !s.inDrafts && <span className="text-faint">—</span>}
          {s.inDrafts > 0 && (
            <span className="font-sans text-[11px] font-normal text-muted">
              {s.onOrder ? " + " : ""}
              {qty(s.inDrafts)} in a draft
            </span>
          )}
        </Stat>
        {r && item.shopify_tracked !== false && (
          <>
            <Stat label="Plan">
              <Badge tone={BUCKET_TONE[r.bucket]}>{BUCKET_LABELS[r.bucket]}</Badge>
            </Stat>
            <Stat label="Order by" danger={r.orderByDay !== null && r.orderByDay < 0}>
              {orderByText(r, plan.today) || <span className="text-faint">—</span>}
            </Stat>
            <Stat label="Cover" title="How long available plus what's on order lasts at the forecast rate">
              {coverText(r)}
            </Stat>
          </>
        )}
      </div>
      {item.source === "local" && <PartProjects item={item} />}
      <JumpChips />
    </div>
  );
}

/**
 * A part's projects, or why it's in none; a click goes to where it's used (the BOM section's Used in), or for a kit
 * nothing uses (in its project through its own BOM's Project, or in none until that's set) to its BOM.
 */
function PartProjects({ item }: { item: Item }) {
  const px = useProjectIndex();
  const pp = usePartProjects().get(item.id);
  const any = !!pp && (pp.inUse.length > 0 || pp.onDraft.length > 0);
  // A sub-assembly no BOM uses: its own BOM's Project decides (root c).
  const kit = item.kind === "subassembly" && !pp?.onBom;
  const hasBom = !!currentRevision(px.ix, `item:${item.id}`);
  const jump = () =>
    (kit ? document.getElementById("sec-bom") : (document.getElementById("sec-used-in") ?? document.getElementById("sec-bom")))?.scrollIntoView({
      block: "start",
      behavior: "smooth",
    });
  return (
    <button
      type="button"
      onClick={jump}
      title={
        kit
          ? "Nothing uses it: a sub-assembly nothing uses is in the project its own BOM's Project names: see its BOM"
          : "A part is in the projects whose BOMs use it: see Used in"
      }
      className="mt-2 flex w-full min-w-0 items-center gap-1.5 rounded text-left hover:bg-hover/60"
    >
      <span className="shrink-0 text-[10px] font-medium tracking-wide text-faint uppercase">Projects</span>
      {any ? (
        <PartProjectChips px={px} id={item.id} link={false} />
      ) : (
        <span className="truncate text-[12px] text-muted">
          {pp?.onBom
            ? "In no project: what uses it isn't in one"
            : kit
              ? hasBom
                ? "Nothing uses it and its BOM names no project: set Project on its BOM"
                : "Nothing uses it: start its BOM and set its Project, or put it on a BOM"
              : "Not on a BOM yet: it joins a project when one of its BOMs uses it"}
        </span>
      )}
    </button>
  );
}

function Stat({ label, title, danger, children }: { label: string; title?: string; danger?: boolean; children: React.ReactNode }) {
  return (
    <div className="min-w-0" title={title}>
      <div className="text-[10px] font-medium tracking-wide text-faint uppercase">{label}</div>
      <div className={cx("truncate font-mono text-[13px] font-semibold", danger && "text-danger", title && "cursor-help")}>{children}</div>
    </div>
  );
}

/** Chips for the panel's sections that have something in them, kept up to date as they fill in or empty. */
function JumpChips() {
  const ref = useRef<HTMLDivElement>(null);
  const [secs, setSecs] = useState<{ id: string; label: string }[]>([]);
  useEffect(() => {
    const root = ref.current?.closest("aside");
    if (!root) return;
    const read = () => {
      const found = [...root.querySelectorAll<HTMLElement>("[data-sec]")]
        .filter((el) => el.childElementCount > 0)
        .map((el) => ({ id: el.id, label: el.dataset.sec ?? "" }));
      setSecs((prev) => (prev.length === found.length && prev.every((p, i) => p.id === found[i].id) ? prev : found));
    };
    read();
    const watch = new MutationObserver(read);
    watch.observe(root, { childList: true, subtree: true });
    return () => watch.disconnect();
  }, []);
  const jump = (id: string) => {
    const el = document.getElementById(id);
    if (el instanceof HTMLDetailsElement) el.open = true;
    el?.scrollIntoView({ block: "start", behavior: "smooth" });
  };
  return (
    <div ref={ref} className="mt-2 flex flex-wrap gap-1">
      {secs.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => jump(s.id)}
          className="h-5 rounded-full border border-line bg-surface px-2 text-[11px] text-muted hover:border-line-strong hover:text-text"
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}
